import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Loader2,
  PanelRightOpen,
  Save,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { LoadingPage } from "@/components/shared/LoadingPage";
import {
  createEnterpriseProduct,
  getEnterpriseProduct,
  updateEnterpriseProduct,
  type EnterpriseProduct,
} from "@/services/platformEnterprisesService";
import { listPlatformCertificateRequestIntents } from "@/services/platformCertificateRequestIntentsService";
import type { ProductNodeType } from "@/services/platformEnterprisesService";

import { ProductBuilderCanvas } from "@/components/features/enterprise/builders/product-builder-canvas";
import { ProductBuilderDetailPanel } from "@/components/features/enterprise/builders/product-builder-detail-panel";
import { ProductBuilderHeaderForm } from "@/components/features/enterprise/builders/product-builder-header-form";
import { ProductBuilderPalette } from "@/components/features/enterprise/builders/product-builder-palette";
import { ProductBuilderValidationStrip } from "@/components/features/enterprise/builders/product-builder-validation-strip";
import { useProductBuilderState } from "@/components/features/enterprise/builders/use-product-builder-state";

export function EnterpriseProductBuilderPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enterpriseId = "", productId } = useParams<{
    enterpriseId: string;
    productId?: string;
  }>();

  const isEditMode = Boolean(productId);

  const productQuery = useQuery({
    queryKey: ["platform", "enterprises", enterpriseId, "products", productId] as const,
    queryFn: () => getEnterpriseProduct(enterpriseId, productId as string),
    enabled: isEditMode,
  });

  if (isEditMode && productQuery.isPending) {
    return <LoadingPage />;
  }

  if (isEditMode && productQuery.isError) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        {t("platformEnterprises.productLoadError")}
      </div>
    );
  }

  return (
    <BuilderView
      enterpriseId={enterpriseId}
      initial={productQuery.data ?? null}
      onBack={() => navigate(`/platform/enterprises/${enterpriseId}`)}
      onCreated={(created) =>
        navigate(
          `/platform/enterprises/${enterpriseId}/products/${created.id}/edit`,
          { replace: true },
        )
      }
      onSaved={() => {
        void queryClient.invalidateQueries({
          queryKey: ["platform", "enterprises", enterpriseId, "products"],
        });
      }}
    />
  );
}

interface BuilderViewProps {
  enterpriseId: string;
  initial: EnterpriseProduct | null;
  onBack: () => void;
  onCreated: (created: EnterpriseProduct) => void;
  onSaved: () => void;
}

function BuilderView({
  enterpriseId,
  initial,
  onBack,
  onCreated,
  onSaved,
}: BuilderViewProps): JSX.Element {
  const { t } = useTranslation();
  const {
    header,
    graph,
    selectedNodeId,
    selectedEdgeId,
    isDirty,
    errors,
    setHeader,
    setSelectedNodeId,
    setSelectedEdgeId,
    addNode,
    updateNodeData,
    updateNodePosition,
    removeNode,
    addEdge,
    removeEdge,
    reconnectEdge,
    toPayload,
    resetDirty,
  } = useProductBuilderState({ initial });

  const [metaOpen, setMetaOpen] = useState(!initial);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setDrawerOpen(Boolean(selectedNodeId));
  }, [selectedNodeId]);

  const intentsQuery = useQuery({
    queryKey: ["platform", "certificate-request-intents"],
    queryFn: listPlatformCertificateRequestIntents,
    staleTime: 5 * 60 * 1000,
  });

  const categorySlugs = useMemo(
    () =>
      (intentsQuery.data ?? [])
        .filter((i) => i.flowKey === "enterprise_catalog")
        .map((i) => i.slug),
    [intentsQuery.data],
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = toPayload();
      if (initial) {
        return updateEnterpriseProduct(enterpriseId, initial.id, payload);
      }
      return createEnterpriseProduct(enterpriseId, payload);
    },
    onSuccess: (product) => {
      toast.success(t("platformEnterprises.productSaved"));
      resetDirty();
      onSaved();
      if (!initial) onCreated(product);
    },
    onError: () => {
      toast.error(t("platformEnterprises.productSaveError"));
    },
  });

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleBack = useCallback((): void => {
    if (isDirty) {
      const ok = window.confirm(t("enterpriseProductBuilder.unsavedLeaveConfirm"));
      if (!ok) return;
    }
    onBack();
  }, [isDirty, onBack, t]);

  const handlePaletteAdd = useCallback(
    (type: ProductNodeType, dataOverrides?: Record<string, unknown>): void => {
      const id = addNode(type, undefined, dataOverrides);
      setSelectedNodeId(id);
    },
    [addNode, setSelectedNodeId],
  );

  const selectedNode = useMemo(
    () => graph.nodes.find((n) => n.id === selectedNodeId) ?? null,
    [graph.nodes, selectedNodeId],
  );

  const canSave =
    !saveMutation.isPending && header.displayName.trim().length > 0 && errors.length === 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex shrink-0 items-center justify-between gap-3 border-b bg-background px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            {t("pages.common.back")}
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="truncate text-lg font-semibold text-foreground">
                {header.displayName ||
                  t(
                    initial
                      ? "enterpriseProductBuilder.editTitle"
                      : "enterpriseProductBuilder.newTitle",
                  )}
              </h1>
              {isDirty && (
                <Badge variant="outline" className="text-xs">
                  {t("enterpriseProductBuilder.unsaved")}
                </Badge>
              )}
            </div>
            {initial?.slug && (
              <code className="text-[11px] text-muted-foreground">{initial.slug}</code>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={!canSave}
          >
            {saveMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {t("enterpriseProductBuilder.save")}
          </Button>
        </div>
      </header>

      <section className="shrink-0 border-b bg-muted/20">
        <button
          type="button"
          onClick={() => setMetaOpen((v) => !v)}
          className="flex w-full items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:bg-muted/40"
        >
          {metaOpen ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
          {t("enterpriseProductBuilder.productDetails")}
        </button>
        {metaOpen && (
          <div className="border-t bg-background px-4 py-4">
            <ProductBuilderHeaderForm
              header={header}
              onChange={setHeader}
              categorySlugs={categorySlugs}
            />
          </div>
        )}
      </section>

      <div className="flex min-h-0 flex-1">
        <aside className="w-[240px] shrink-0 overflow-y-auto border-r bg-background">
          <ProductBuilderPalette onAdd={handlePaletteAdd} />
        </aside>

        <main className="relative min-w-0 flex-1 overflow-hidden bg-muted/30">
          <ProductBuilderCanvas
            graph={graph}
            selectedNodeId={selectedNodeId}
            selectedEdgeId={selectedEdgeId}
            errors={errors}
            onSelectNode={setSelectedNodeId}
            onSelectEdge={setSelectedEdgeId}
            onMoveNode={updateNodePosition}
            onAddEdge={addEdge}
            onRemoveNode={removeNode}
            onRemoveEdge={removeEdge}
            onReconnectEdge={reconnectEdge}
          />

          {!drawerOpen && selectedNode && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="absolute right-3 top-3 z-10 shadow-md transition-opacity hover:opacity-100"
              onClick={() => setDrawerOpen(true)}
            >
              <PanelRightOpen className="mr-1.5 h-4 w-4" />
              {t("enterpriseProductBuilder.editNode")}
            </Button>
          )}

          <aside
            className={cn(
              "absolute right-0 top-0 z-20 flex h-full w-[min(560px,90vw)] flex-col border-l bg-background shadow-xl transition-transform duration-200 ease-out motion-reduce:transition-none",
              drawerOpen ? "translate-x-0" : "translate-x-full",
            )}
            aria-hidden={!drawerOpen}
          >
            <ProductBuilderDetailPanel
              graph={graph}
              node={selectedNode}
              onUpdateData={updateNodeData}
              onRemoveNode={(id) => {
                removeNode(id);
                setSelectedNodeId(null);
              }}
              onClose={() => setDrawerOpen(false)}
              enterpriseId={enterpriseId}
              productId={initial?.id ?? ""}
            />
          </aside>
        </main>
      </div>

      <ProductBuilderValidationStrip
        errors={errors}
        onSelectNode={setSelectedNodeId}
      />
    </div>
  );
}
